"use client";

import { DataTable, EmptyState } from "@/components/shared";
import { ExternalLink } from "lucide-react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import type { PublisherEventFeedItem } from "../types";
import { publisherEventsColumns } from "../config";
import {
  formatPublisherEventText,
  getPublisherEventLabel,
  getPublisherEventTone,
} from "../utils/formatPublisherEventText";
import { formatDateShort, formatRelativeTime } from "@/lib/utils/date";

function getEventKey(e: PublisherEventFeedItem): string {
  return [
    e.ts,
    e.event_type,
    e.source,
    e.appid ?? "publisher",
    e.value ?? "null",
    e.prev_value ?? "null",
  ].join(":");
}

function renderEventTextWithHighlights(e: PublisherEventFeedItem) {
  const text = formatPublisherEventText(e);
  const tone = getPublisherEventTone(e.event_type);
  if (tone === "wishlist" || tone === "topsellers") {
    return text.split(/(#[0-9]+)/g).map((part, idx) =>
      /^#[0-9]+$/.test(part) ? (
        <span key={idx} className="text-[#66c0f4] font-semibold">
          {part}
        </span>
      ) : (
        <span key={idx}>{part}</span>
      )
    );
  }
  if (tone === "ccu") {
    return text.split(/([0-9]+)/g).map((part, idx) =>
      /^[0-9]+$/.test(part) ? (
        <span key={idx} className="text-[#a4d007] font-semibold">
          {part}
        </span>
      ) : (
        <span key={idx}>{part}</span>
      )
    );
  }
  return text;
}

interface PublisherEventsListProps {
  events: PublisherEventFeedItem[];
}

function MobileGameCell({
  appid,
  gameName,
}: {
  appid: number;
  gameName: string | null;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleFilterByGame = (e: React.MouseEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    params.set("game_filter", appid.toString());
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const handleOpenInNewTab = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(`/app/${appid}`, "_blank");
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleFilterByGame}
        className="text-[#66c0f4] hover:text-white transition-colors text-xs text-left"
      >
        {gameName ?? `App ${appid}`}
      </button>
      <button
        onClick={handleOpenInNewTab}
        className="text-[#8f98a0] hover:text-[#66c0f4] transition-colors flex-shrink-0"
        title="Open in new tab"
      >
        <ExternalLink className="w-3 h-3" />
      </button>
    </div>
  );
}

export function PublisherEventsList({ events }: PublisherEventsListProps) {
  if (events.length === 0) {
    return <EmptyState message="No events found for this publisher." />;
  }

  return (
    <div className="bg-[#16202d] rounded-lg border border-[#2a475e] overflow-hidden">
      <DataTable
        data={events}
        columns={publisherEventsColumns}
        getRowKey={getEventKey}
      />

      {/* Mobile list */}
      <div className="md:hidden">
        {events.map((e) => (
          <div
            key={getEventKey(e)}
            className="border-b border-[#2a475e] last:border-b-0 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white text-sm">
                  {renderEventTextWithHighlights(e)}
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-[#8f98a0]">
                    {e.event_day || formatDateShort(e.ts)}
                  </span>
                  <span className="text-[10px] text-[#8f98a0]">
                    {formatRelativeTime(e.ts)}
                  </span>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded border bg-[#1b2838] ${
                      getPublisherEventTone(e.event_type) === "wishlist"
                        ? "text-[#b18cff] border-[#5a3d7a]"
                        : getPublisherEventTone(e.event_type) === "topsellers"
                        ? "text-[#ffcc6f] border-[#6b5530]"
                        : getPublisherEventTone(e.event_type) === "ccu"
                        ? "text-[#a4d007] border-[#3b4a1a]"
                        : "text-[#c7d5e0] border-[#2a475e]"
                    }`}
                  >
                    {getPublisherEventLabel(e.event_type)}
                  </span>
                </div>
              </div>

              <div className="flex-shrink-0 text-right">
                {e.appid != null ? (
                  <MobileGameCell appid={e.appid} gameName={e.game_name} />
                ) : (
                  <span className="text-[#8f98a0] text-xs">—</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
